import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Search, Plus, ArrowUpRight, ArrowDownLeft, Download, Landmark, Pencil, Trash2, ArrowDown, ArrowUp, X, Calendar
} from 'lucide-react';
import { exportToPDF } from '@/utils/exportPDF';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useOperationalExpenses } from '@/hooks/useOperationalExpenses';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  let str = String(dateStr).trim();
  if (/^[A-Za-z]{3}\s+[A-Za-z]{3}\s+\d{1,2}\s+\d{4}/.test(str)) {
    let parseable = str.replace('Agt', 'Aug').replace('Mei', 'May').replace('Okt', 'Oct').replace('Des', 'Dec');
    const d = new Date(parseable);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, '0');
      const m = String(d.getMonth() + 1).padStart(2, '0');
      return `${day}-${m}-${d.getFullYear()}`;
    }
  }
  const dateObj = new Date(dateStr);
  if (!isNaN(dateObj.getTime())) {
    const d = String(dateObj.getDate()).padStart(2, '0');
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const y = dateObj.getFullYear();
    return `${d}-${m}-${y}`;
  }
  if (dateStr.includes('/')) {
    let [d, m, y] = dateStr.split('/');
    if (d.length === 1) d = '0' + d;
    if (m.length === 1) m = '0' + m;
    return `${d}-${m}-${y}`;
  }
  if (dateStr.includes('-')) {
    const [y, m, d] = dateStr.split('-');
    if (y.length === 4) return `${d}-${m}-${y}`;
  }
  return dateStr;
};

const parseDateToTime = (dateStr, fallbackCreatedDate = null) => {
  if (!dateStr) return fallbackCreatedDate ? new Date(fallbackCreatedDate).getTime() || 0 : 0;
  
  let str = String(dateStr).trim();
  
  if (/^[A-Za-z]{3}\s+[A-Za-z]{3}\s+\d{1,2}\s+\d{4}/.test(str)) {
    let parseable = str.replace('Agt', 'Aug').replace('Mei', 'May').replace('Okt', 'Oct').replace('Des', 'Dec');
    const d = new Date(parseable);
    if (!isNaN(d.getTime())) return d.getTime();
  }

  if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(str)) {
    const parts = str.split(/[-/]/);
    const d = new Date(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`);
    if (!isNaN(d.getTime())) return d.getTime();
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const d = new Date(str);
    if (!isNaN(d.getTime())) return d.getTime();
  }

  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d.getTime();

  return fallbackCreatedDate ? new Date(fallbackCreatedDate).getTime() || 0 : 0;
};

const normalizeDateForPicker = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('/')) {
    let [d, m, y] = dateStr.split('/');
    if (d.length === 1) d = '0' + d;
    if (m.length === 1) m = '0' + m;
    return `${y}-${m}-${d}`;
  }
  return dateStr;
};

const normalizeDateToISO = (dateStr) => {
  if (!dateStr) return '';
  const str = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return str;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    return str.substring(0, 10);
  }
  if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(str)) {
    const parts = str.split(/[-/]/);
    const day = parts[0].padStart(2, '0');
    const month = parts[1].padStart(2, '0');
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }
  const time = parseDateToTime(str);
  if (!time) return '';
  const d = new Date(time);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function FinanceAccounts() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('semua'); // 'semua' | 'masuk' | 'keluar'
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = terbaru ke lama, 'asc' = lama ke terbaru
  const [search, setSearch] = useState('');
  const [searchGlobalDates, setSearchGlobalDates] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const { clientPOs, isLoading: loadingPOs } = useClientPOs();
  const { operationalExpenses = [], isLoading: loadingExpenses } = useOperationalExpenses();

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Bank Accounts Settings from hook
  const { accountsMap: accounts, createAccount, updateAccount, deleteAccount, isLoading: loadingAccounts } = useBankAccounts();


  // Account editing modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({ nama_bank: '', name: '', number: '', startBalance: '', label: '' });
  const [isCreatingNewAccount, setIsCreatingNewAccount] = useState(false);

  // Mutation editing modal states
  const [showEditMutationModal, setShowEditMutationModal] = useState(false);
  const [editMutationData, setEditMutationData] = useState(null);

  // Account filter for mutations table
  const [selectedAccountFilter, setSelectedAccountFilter] = useState('all'); // 'all' | 'bca' | 'mandiri' | 'bni'

  const KATEGORI_OPTIONS = [
    'Bahan Baku',
    'Operasional',
    'Gaji Karyawan',
    'Ongkir / Ekspedisi',
    'Pembayaran Invoice',
    'Pembelian Aset',
    'Lainnya'
  ];

  // Form State for manual transaction
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'masuk',
    account: '', // initialized dynamically to accounts.bca.name
    amount: '',
    description: '',
    kategori: '',
    coa_allocations: [{ prefix: '', account_id: '', amount: '' }]
  });

  // Dynamically set default form account based on loaded accounts
  useEffect(() => {
    const firstAccountKey = Object.keys(accounts)[0];
    if (firstAccountKey && accounts[firstAccountKey]?.name) {
      setForm(f => {
        if (!f.account) return { ...f, account: accounts[firstAccountKey].name };
        return f;
      });
    }
  }, [accounts]);

  // Fetch Mutations
  const { data: dbMutations = [], isLoading } = useQuery({
    queryKey: ['account-mutations'],
    queryFn: () => base44.entities.AccountMutation.list('-date')
  });

  // Fetch Chart of Accounts for multi-COA selection
  const { data: coaList = [] } = useQuery({
    queryKey: ['chart-of-accounts'],
    queryFn: () => base44.entities.ChartOfAccount.list('id')
  });

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount,
    totalCount
  } = useBatchSelection(dbMutations, m => m.id);

  // Fetch Barang Keluar (to sync shipping costs/ongkir)
  const { data: barangKeluar = [] } = useQuery({
    queryKey: ['barang-keluar'],
    queryFn: () => base44.entities.BarangKeluar.list('-created_date')
  });

  // Fetch Riwayat Produksi (HPP)
  const { data: riwayatProduksi = [] } = useQuery({
    queryKey: ['riwayat-produksi'],
    queryFn: () => base44.entities.RiwayatProduksi.list('-created_date')
  });

  // Create Manual Mutation
  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.AccountMutation.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
      toast.success('Transaksi manual berhasil dicatat!');
      setShowAddModal(false);
      setForm({
        date: new Date().toISOString().split('T')[0],
        type: 'masuk',
        account: accounts.bca.name,
        amount: '',
        description: '',
        kategori: '',
        coa_allocations: [{ bank_account: accounts.bca?.name || '', prefix: '', account_id: '', amount: '' }]
      });
    }
  });

  const updateMutationRecord = useMutation({
    mutationFn: ({ id, data }) => base44.entities.AccountMutation.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
      toast.success('Transaksi mutasi berhasil diperbarui!');
      setShowEditMutationModal(false);
      setEditMutationData(null);
    }
  });

  const deleteMutationRecord = useMutation({
    mutationFn: (id) => base44.entities.AccountMutation.hardDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
      toast.success('Transaksi mutasi berhasil dihapus!');
    }
  });

  const batchDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        await base44.entities.AccountMutation.hardDelete(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
      toast.success(`${selectedCount} transaksi berhasil dihapus!`);
      clearSelection();
    }
  });

  // Auto-sync function completely removed to switch to Push (Event-Driven) architecture.

  // Helper mapping function to safely relate mutation records back to account configuration keys
  const getAccountKeyOfMutation = (mutationAccountName) => {
    const name = (mutationAccountName || '').toLowerCase().trim();
    for (const key of Object.keys(accounts)) {
      const acc = accounts[key];
      if (!acc) continue;
      const accName = (acc.name || '').toLowerCase().trim();
      const accBank = (acc.nama_bank || '').toLowerCase().trim();
      if (name === accName || name === accBank || key.toLowerCase() === name) return key;
    }
    
    // Substring matcher fallbacks for custom accounts
    for (const key of Object.keys(accounts)) {
      const acc = accounts[key];
      if (!acc) continue;
      const accName = (acc.name || '').toLowerCase().trim();
      if (accName && (name.includes(accName) || accName.includes(name))) return key;
    }

    if (name.includes('bca')) return 'bca';
    if (name.includes('mandiri') || name.includes('qris')) return 'mandiri';
    if (name.includes('bni')) return 'bni';
    return Object.keys(accounts)[0] || 'bca';
  };

  // Deduplikasi ketat & sertakan seluruh transaksi mutasi yang valid/aktif (termasuk yang sudah dikonfirmasi)
  const uniqueVerifiedMutations = useMemo(() => {
    const seen = new Set();
    const result = [];
    (dbMutations || []).forEach(m => {
      if (!m || !m.id || m.status === 'rejected' || m.status === 'cancelled') return;
      if (seen.has(m.id)) return;
      seen.add(m.id);
      result.push(m);
    });
    return result;
  }, [dbMutations]);

  // Compute live balances per account based on starting balance + mutations
  const accountBalances = useMemo(() => {
    const balances = {};
    Object.keys(accounts).forEach(k => {
      balances[k] = Number(accounts[k]?.startBalance) || 0;
    });

    uniqueVerifiedMutations.forEach(m => {
      const key = getAccountKeyOfMutation(m.account);
      const amt = Number(m.amount) || 0;
      if (balances[key] === undefined) {
        balances[key] = Number(accounts[key]?.startBalance) || 0;
      }
      if (m.type === 'masuk') {
        balances[key] = (balances[key] || 0) + amt;
      } else {
        balances[key] = (balances[key] || 0) - amt;
      }
    });
    return balances;
  }, [uniqueVerifiedMutations, accounts]);

  const totalBalance = useMemo(() => {
    return Object.values(accountBalances).reduce((sum, b) => sum + b, 0);
  }, [accountBalances]);

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedAccountFilter, dateFrom, dateTo, search, activeTab]);

  // Mutations filtered strictly by selected bank and date range (ignores activeTab & search)
  // so Pemasukan and Pengeluaran always give the true financial total for the selected bank & dates
  const periodFilteredMutations = useMemo(() => {
    return uniqueVerifiedMutations.filter(m => {
      const key = getAccountKeyOfMutation(m.account);
      const matchAccount = selectedAccountFilter === 'all' || key === selectedAccountFilter;
      const mDate = normalizeDateToISO(m.date);
      const matchFrom = !dateFrom || (mDate && mDate >= dateFrom);
      const matchTo = !dateTo || (mDate && mDate <= dateTo);
      return matchAccount && matchFrom && matchTo;
    });
  }, [uniqueVerifiedMutations, selectedAccountFilter, dateFrom, dateTo, accounts]);

  const summaryIncome = useMemo(() => {
    return periodFilteredMutations
      .filter(m => m.type === 'masuk')
      .reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
  }, [periodFilteredMutations]);

  const summaryExpense = useMemo(() => {
    return periodFilteredMutations
      .filter(m => m.type === 'keluar')
      .reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
  }, [periodFilteredMutations]);

  const summaryNet = useMemo(() => {
    return summaryIncome - summaryExpense;
  }, [summaryIncome, summaryExpense]);

  const displayTotalBalance = useMemo(() => {
    if (selectedAccountFilter !== 'all') {
      return accountBalances[selectedAccountFilter] || 0;
    }
    return totalBalance;
  }, [selectedAccountFilter, accountBalances, totalBalance]);

  const displayTotalLabel = useMemo(() => {
    if (selectedAccountFilter !== 'all') {
      const accName = accounts[selectedAccountFilter]?.name || 'Rekening';
      return `Saldo Kas ${accName}`;
    }
    return 'Total Kas Satala Dermatech';
  }, [selectedAccountFilter, accounts]);

  const periodTransactionStats = useMemo(() => {
    let masukCount = 0;
    let keluarCount = 0;
    periodFilteredMutations.forEach(m => {
      if (m.type === 'masuk') masukCount++;
      else if (m.type === 'keluar') keluarCount++;
    });
    return {
      total: periodFilteredMutations.length,
      masukCount,
      keluarCount
    };
  }, [periodFilteredMutations]);

  // Posisi kondisi saldo kas pada akhir periode / tanggal tertentu yang dipilih
  const periodClosingBalance = useMemo(() => {
    if (!dateTo && !dateFrom) return displayTotalBalance;
    
    // Gunakan dateTo sebagai batas akhir saldo, atau dateFrom jika hanya dateFrom yang dipilih
    const cutoffDate = dateTo || dateFrom;
    
    let bal = 0;
    if (selectedAccountFilter !== 'all') {
      bal = Number(accounts[selectedAccountFilter]?.startBalance) || 0;
    } else {
      Object.keys(accounts).forEach(k => {
        bal += Number(accounts[k]?.startBalance) || 0;
      });
    }

    uniqueVerifiedMutations.forEach(m => {
      const key = getAccountKeyOfMutation(m.account);
      const matchAccount = selectedAccountFilter === 'all' || key === selectedAccountFilter;
      if (!matchAccount) return;

      const mDate = normalizeDateToISO(m.date);
      if (mDate && mDate <= cutoffDate) {
        const amt = Number(m.amount) || 0;
        if (m.type === 'masuk') bal += amt;
        else bal -= amt;
      }
    });

    return bal;
  }, [dateFrom, dateTo, selectedAccountFilter, accounts, uniqueVerifiedMutations, displayTotalBalance]);

  // Filter mutations with selectedAccountFilter and powerful omni-search support
  const filteredMutations = useMemo(() => {
    const cleanSearch = (search || '').trim().toLowerCase();
    const cleanNumeric = cleanSearch.replace(/[^0-9]/g, '');

    return uniqueVerifiedMutations.filter(m => {
      const matchTab = activeTab === 'semua' || m.type === activeTab;
      
      const key = getAccountKeyOfMutation(m.account);
      const matchAccountFilter = selectedAccountFilter === 'all' || key === selectedAccountFilter;

      let matchSearch = true;
      if (cleanSearch) {
        const desc = (m.description || '').toLowerCase();
        const acc = (m.account || '').toLowerCase();
        const cat = (m.category || '').toLowerCase();
        const subcat = (m.subcategory || '').toLowerCase();
        const notes = (m.notes || '').toLowerCase();
        const pic = (m.pic || '').toLowerCase();
        const refNo = (m.reference_no || '').toLowerCase();
        const dt = (m.date || '').toLowerCase();

        const fullText = `${desc} ${acc} ${cat} ${subcat} ${notes} ${pic} ${refNo} ${dt}`;
        const textMatch = fullText.includes(cleanSearch);

        let numMatch = false;
        if (cleanNumeric && cleanNumeric.length >= 2) {
          const amtStr = String(Math.round(Number(m.amount) || 0));
          const formattedAmt = Number(m.amount || 0).toLocaleString('id-ID');
          numMatch = amtStr.includes(cleanNumeric) || formattedAmt.includes(cleanSearch);
        }

        matchSearch = textMatch || numMatch;
      }
        
      const mDate = normalizeDateToISO(m.date);
      const isDateConstrained = !searchGlobalDates && (dateFrom || dateTo);
      const matchFrom = !isDateConstrained || !dateFrom || (mDate && mDate >= dateFrom);
      const matchTo = !isDateConstrained || !dateTo || (mDate && mDate <= dateTo);
      
      return matchTab && matchAccountFilter && matchSearch && matchFrom && matchTo;
    }).sort((a, b) => {
      const dateA = parseDateToTime(a.date, a.created_date || a.created_at);
      const dateB = parseDateToTime(b.date, b.created_date || b.created_at);
      const timeA = (a.created_date || a.created_at) ? new Date(a.created_date || a.created_at).getTime() : 0;
      const timeB = (b.created_date || b.created_at) ? new Date(b.created_date || b.created_at).getTime() : 0;
      
      if (sortOrder === 'desc') {
        if (dateA !== dateB) return dateB - dateA;
        if (timeA !== timeB) return timeB - timeA;
        return (b.id || '').localeCompare(a.id || '');
      } else {
        if (dateA !== dateB) return dateA - dateB;
        if (timeA !== timeB) return timeA - timeB;
        return (a.id || '').localeCompare(b.id || '');
      }
    });
  }, [uniqueVerifiedMutations, activeTab, selectedAccountFilter, search, searchGlobalDates, dateFrom, dateTo, accounts, sortOrder]);

  const searchSummary = useMemo(() => {
    if (!search.trim()) return null;
    let masuk = 0;
    let keluar = 0;
    filteredMutations.forEach(m => {
      const amt = Number(m.amount) || 0;
      if (m.type === 'masuk') masuk += amt;
      else if (m.type === 'keluar') keluar += amt;
    });
    return {
      count: filteredMutations.length,
      masuk,
      keluar,
      net: masuk - keluar
    };
  }, [search, filteredMutations]);

  const totalPages = Math.ceil(filteredMutations.length / itemsPerPage) || 1;
  const paginatedMutations = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredMutations.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredMutations, currentPage, itemsPerPage]);

  // Export PDF
  const handleExportPDF = () => {
    exportToPDF({
      title: 'Laporan Rekening & Mutasi Saldo Satala',
      subtitle: `Periode: ${dateFrom || 'Semua'} s/d ${dateTo || 'Semua'} | Dicetak pada: ${new Date().toLocaleDateString('id-ID')}`,
      filename: `Mutasi-Rekening-${new Date().toISOString().split('T')[0]}`,
      columns: [
        { key: 'date', label: 'Tanggal', render: r => r.date },
        { key: 'account', label: 'Rekening Bank', render: r => r.account },
        { key: 'type', label: 'Jenis', render: r => r.type === 'masuk' ? 'Uang Masuk (Kredit)' : 'Uang Keluar (Debit)' },
        { key: 'description', label: 'Keterangan', render: r => r.description },
        { key: 'amount', label: 'Jumlah (Rp)', render: r => `${r.type === 'masuk' ? '+' : '-'}Rp ${fmt(r.amount)}` }
      ],
      rows: filteredMutations
    });
  };

  const handleSaveManual = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('Mohon isi jumlah nominal dengan benar.');
      return;
    }
    
    // Validate COA Allocations
    const validCoas = (form.coa_allocations || []).filter(c => c.account_id && Number(c.amount) > 0 && c.bank_account);
    if (validCoas.length > 0) {
      const totalCoa = validCoas.reduce((sum, c) => sum + Number(c.amount), 0);
      if (totalCoa !== Number(form.amount)) {
        toast.error(`Total nominal COA (Rp ${fmt(totalCoa)}) harus sama dengan Jumlah Transaksi (Rp ${fmt(form.amount)})!`);
        return;
      }
      
      // Group by Bank Account
      const grouped = {};
      validCoas.forEach(c => {
        if (!grouped[c.bank_account]) {
           grouped[c.bank_account] = { amount: 0, allocations: [] };
        }
        grouped[c.bank_account].amount += Number(c.amount);
        grouped[c.bank_account].allocations.push({ ...c });
      });
      
      // Create multiple mutations
      try {
        for (const [bank, data] of Object.entries(grouped)) {
          await base44.entities.AccountMutation.create({
            date: form.date,
            type: form.type,
            account: bank,
            amount: data.amount,
            description: form.description || 'Transaksi Manual',
            kategori: form.kategori || '',
            coa_allocations: data.allocations
          });
        }
        queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
        toast.success('Transaksi manual berhasil dicatat (Telah dibagi ke masing-masing Bank)!');
        setShowAddModal(false);
        setForm({
          date: new Date().toISOString().split('T')[0],
          type: 'masuk',
          account: '',
          amount: '',
          description: '',
          kategori: '',
          coa_allocations: [{ bank_account: accounts.bca?.name || '', prefix: '', account_id: '', amount: '' }]
        });
      } catch (e) {
         console.error(e);
         toast.error('Gagal mencatat transaksi.');
      }
    } else {
      toast.error('Mohon isi minimal satu alokasi COA dengan nominal lebih dari 0 dan pilih Bank masing-masing.');
    }
  };

  const handleUpdateMutation = (e) => {
    e.preventDefault();
    if (!editMutationData.amount || Number(editMutationData.amount) <= 0) {
      toast.error('Mohon isi jumlah nominal dengan benar.');
      return;
    }
    
    // Validate COA Allocations
    const validCoas = (editMutationData.coa_allocations || []).filter(c => c.account_id && Number(c.amount) > 0);
    if (validCoas.length > 0) {
      const totalCoa = validCoas.reduce((sum, c) => sum + Number(c.amount), 0);
      if (totalCoa !== Number(editMutationData.amount)) {
        toast.error(`Total nominal COA (Rp ${fmt(totalCoa)}) harus sama dengan Jumlah Transaksi (Rp ${fmt(editMutationData.amount)})!`);
        return;
      }
    }

    updateMutationRecord.mutate({
      id: editMutationData.id,
      data: {
        date: editMutationData.date,
        type: editMutationData.type,
        account: editMutationData.account,
        amount: Number(editMutationData.amount),
        description: editMutationData.description || 'Transaksi Manual',
        coa_allocations: validCoas,
        kategori: editMutationData.kategori || ''
      }
    });
  };

  // Open Edit Rekening Modal
  const openEditModal = (key) => {
    setEditingKey(key);
    setIsCreatingNewAccount(false);
    setEditForm({
      nama_bank: accounts[key].nama_bank || key,
      name: accounts[key].name || '',
      number: accounts[key].number || '',
      startBalance: accounts[key].startBalance || 0,
      label: accounts[key].label || ''
    });
    setShowEditModal(true);
  };

  const openAddAccountModal = () => {
    setEditingKey(null);
    setIsCreatingNewAccount(true);
    setEditForm({
      nama_bank: '',
      name: '',
      number: '',
      startBalance: 0,
      label: ''
    });
    setShowEditModal(true);
  };

  // Save Edit Rekening Settings
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      const cleanBalance = String(editForm.startBalance).replace(/[^0-9-]/g, '');
      const bankData = {
        nama_bank: editForm.nama_bank,
        name: editForm.name,
        number: editForm.number,
        startBalance: Number(cleanBalance) || 0,
        label: editForm.label
      };
      
      if (isCreatingNewAccount) {
         await createAccount(bankData);
         toast.success('Rekening baru berhasil ditambahkan!');
      } else {
         await updateAccount(editingKey, bankData);
         toast.success('Informasi rekening berhasil diperbarui!');
      }
      setShowEditModal(false);
    } catch (err) {
      console.error('Save account error:', err);
      toast.error('Gagal menyimpan rekening. Coba lagi.');
    }
  };

  const handleDeleteAccount = async () => {
    if (!editingKey) return;
    if (confirm(`Yakin ingin menghapus rekening ${editForm.name}? Ini tidak akan menghapus riwayat mutasi yang sudah ada.`)) {
      try {
        await deleteAccount(editingKey);
        setShowEditModal(false);
        toast.success('Rekening berhasil dihapus!');
      } catch (err) {
         toast.error('Gagal menghapus rekening.');
      }
    }
  };

  return (
    <div className="space-y-8 pb-16 text-[#302621]">
      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedCount}
        totalCount={dbMutations.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={() => {
          if (confirm(`Yakin ingin menghapus ${selectedCount} mutasi terpilih? Saldo akan disesuaikan kembali.`)) {
            batchDeleteMutation.mutate(Array.from(selectedIds));
          }
        }}
        isDeleting={batchDeleteMutation.isPending}
      />
      
      {/* Header Panel */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 md:gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-black tracking-tight text-slate-800">Rekening & Saldo Kas</h1>
          <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 md:mt-1">
            Monitoring saldo real-time 3 rekening bank utama, mutasi invoice masuk, dan pencatatan biaya pengeluaran operasional.
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            className="text-[#8C6208] border-[#8C6208] hover:bg-[#F9F5EC] gap-2 text-xs font-bold rounded-xl h-10 shadow-sm"
            onClick={openAddAccountModal}
          >
            <Plus className="w-4 h-4" /> Tambah Rekening
          </Button>
          <Button 
            className="bg-[#8C6208] text-white hover:bg-[#724F06] gap-2 text-xs font-bold rounded-xl h-10 shadow-sm"
            onClick={() => setShowAddModal(true)}
          >
            <Plus className="w-4 h-4" /> Catat Transaksi
          </Button>
        </div>
      </div>

      {/* ATM Cards visualizer */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {Object.keys(accounts).map((key, idx) => {
          const account = accounts[key];
          const balance = accountBalances[key] || 0;
          const isSelected = selectedAccountFilter === key;
          
          // Generate pseudo-random colors based on index
          const colors = [
            "from-[#1E293B] via-[#0F172A] to-[#1E293B] border-slate-800/80 text-white",
            "from-[#0F5C80] via-[#0A415C] to-[#0A3D56] border-[#0F5C80]/30 text-white",
            "from-[#A5471B] via-[#8C3A12] to-[#702C0A] border-[#A5471B]/30 text-white",
            "from-purple-900 via-purple-800 to-purple-950 border-purple-800/50 text-white",
            "from-emerald-900 via-emerald-800 to-emerald-950 border-emerald-800/50 text-white"
          ];
          const colorClass = colors[idx % colors.length];

          return (
            <div 
              key={key} 
              onClick={() => setSelectedAccountFilter(prev => prev === key ? 'all' : key)}
              className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colorClass} p-4 md:p-6 shadow-xl border cursor-pointer transition-all duration-300 ${
                isSelected 
                  ? 'ring-4 ring-[#8C6208] ring-offset-2 scale-[1.02] shadow-2xl' 
                  : 'hover:-translate-y-1 hover:shadow-2xl opacity-90 hover:opacity-100'
              }`}
              title={isSelected ? 'Klik untuk batal memilih rekening ini' : `Klik untuk filter mutasi & ringkasan ${account.name}`}
            >
              <div className="absolute top-0 right-0 p-4 opacity-5">
                <Landmark className="w-32 h-32" />
              </div>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  openEditModal(key);
                }}
                className="absolute top-4 right-4 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors z-20"
                title="Edit Rekening"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              
              <div className="relative z-10 flex flex-col justify-between h-32 md:h-40">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-[10px] text-white/70 font-bold uppercase tracking-widest">{account.name}</p>
                      {isSelected && (
                        <span className="bg-[#8C6208] text-white text-[8px] font-black px-1.5 py-0.5 rounded shadow-sm">TERPILIH</span>
                      )}
                    </div>
                    <p className="text-[10px] md:text-xs font-mono text-white/50 mt-0.5">{account.number || 'N/A'}</p>
                  </div>
                  <div className="w-8 h-6 md:w-9 md:h-7 rounded bg-white/20 flex items-center justify-center border border-white/10 mr-10">
                    <span className="text-[8px] md:text-[9px] font-black text-white">CHIP</span>
                  </div>
                </div>
                
                <div>
                  <p className="text-[9px] md:text-[10px] text-white/60 uppercase font-medium">{account.label || 'Saldo'}</p>
                  <p className="text-xl md:text-2xl font-black tracking-tight text-white mt-1">Rp {fmt(balance)}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Overall stats & Custom Period Breakdown */}
      <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden">
        <CardContent className="p-4 md:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-center">
            
            {/* Saldo Real-Time Bank Saat Ini */}
            <div className={`space-y-1.5 ${dateFrom || dateTo ? 'lg:col-span-4' : 'lg:col-span-5'}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold tracking-wider uppercase flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Saldo Real-Time
                </span>
                {selectedAccountFilter !== 'all' && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-[#8C6208] text-[10px] font-black tracking-wide border border-amber-200">
                    {accounts[selectedAccountFilter]?.name}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">
                {displayTotalLabel}
              </p>
              <p className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
                Rp {fmt(displayTotalBalance)}
              </p>
              <p className="text-[10px] text-slate-400">
                Posisi saldo kas riil per hari ini (termasuk mutasi berjalan).
              </p>
            </div>

            {/* Custom Period Highlight Panel (Menampilkan posisi uang di periode/tanggal yang dipilih) */}
            {(dateFrom || dateTo) && (
              <div className="lg:col-span-3 bg-amber-50/70 rounded-xl p-3.5 border border-amber-200/80 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black text-amber-900 uppercase tracking-wider flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-[#8C6208]" />
                    Saldo Periode Terpilih
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-amber-200/70 text-amber-900 text-[9px] font-black">
                    {periodTransactionStats.total} Transaksi
                  </span>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 font-bold">
                    {dateTo ? `Posisi Kas s/d ${formatDate(dateTo)}:` : `Posisi Kas Mulai ${formatDate(dateFrom)}:`}
                  </p>
                  <p className="text-xl md:text-2xl font-black text-[#8C6208] tracking-tight">
                    Rp {fmt(periodClosingBalance)}
                  </p>
                </div>
                <div className="text-[10px] text-slate-600 flex items-center justify-between border-t border-amber-200/60 pt-1.5 flex-wrap gap-1">
                  <span>
                    Arus Kas: <strong className={summaryNet >= 0 ? 'text-emerald-700 font-black' : 'text-rose-700 font-black'}>{summaryNet >= 0 ? '+' : ''}Rp {fmt(summaryNet)}</strong>
                  </span>
                  <span className="text-slate-400">
                    ({periodTransactionStats.masukCount} Msk / {periodTransactionStats.keluarCount} Klr)
                  </span>
                </div>
              </div>
            )}

            {/* Pemasukan & Pengeluaran Cards */}
            <div className={`flex flex-col sm:flex-row gap-3 border-t lg:border-t-0 lg:border-l border-slate-100 pt-4 lg:pt-0 lg:pl-5 ${dateFrom || dateTo ? 'lg:col-span-5' : 'lg:col-span-7'}`}>
              <div className="flex-1 bg-emerald-50/50 rounded-xl p-3 border border-emerald-100/80 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold shrink-0">
                  <ArrowUpRight className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">
                    {dateFrom || dateTo 
                      ? 'Pemasukan Periode Ini' 
                      : (selectedAccountFilter !== 'all' ? 'Pemasukan Rekening' : 'Pemasukan Total')}
                  </p>
                  <p className="text-base font-black text-emerald-700 truncate">
                    Rp {fmt(summaryIncome)}
                  </p>
                  {(dateFrom || dateTo) && (
                    <p className="text-[10px] text-emerald-600 font-medium">
                      {periodTransactionStats.masukCount} transaksi masuk
                    </p>
                  )}
                </div>
              </div>

              <div className="flex-1 bg-rose-50/50 rounded-xl p-3 border border-rose-100/80 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center font-bold shrink-0">
                  <ArrowDownLeft className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">
                    {dateFrom || dateTo 
                      ? 'Pengeluaran Periode Ini' 
                      : (selectedAccountFilter !== 'all' ? 'Pengeluaran Rekening' : 'Pengeluaran Total')}
                  </p>
                  <p className="text-base font-black text-rose-700 truncate">
                    Rp {fmt(summaryExpense)}
                  </p>
                  {(dateFrom || dateTo) && (
                    <p className="text-[10px] text-rose-600 font-medium">
                      {periodTransactionStats.keluarCount} transaksi keluar
                    </p>
                  )}
                </div>
              </div>
            </div>

          </div>
        </CardContent>
      </Card>

      {/* Mutation log panel */}
      <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden">
        <div className="p-4 md:p-6 border-b border-slate-100 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 md:gap-4 bg-slate-50/50">
          <div>
            <h3 className="text-sm md:text-base font-bold text-slate-800">Riwayat Mutasi Saldo Rekening</h3>
            <p className="text-[9px] md:text-xs text-slate-500">Hasil penyaringan mutasi kas, auto-sync invoice, dan ongkir pengiriman.</p>
          </div>
          
          <div className="flex flex-wrap gap-2 w-full lg:w-auto items-center">
            {/* Tab sub menus */}
            <div className="flex bg-slate-200/70 p-1 rounded-xl border border-slate-200">
              <button
                onClick={() => setActiveTab('semua')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${activeTab === 'semua' ? 'bg-white text-slate-800 shadow-xs' : 'text-slate-500 hover:text-slate-800'}`}
              >
                Semua
              </button>
              <button
                onClick={() => setActiveTab('masuk')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${activeTab === 'masuk' ? 'bg-white text-emerald-700 shadow-xs' : 'text-slate-500 hover:text-emerald-700'}`}
              >
                Uang Masuk
              </button>
              <button
                onClick={() => setActiveTab('keluar')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${activeTab === 'keluar' ? 'bg-white text-rose-700 shadow-xs' : 'text-slate-500 hover:text-rose-700'}`}
              >
                Uang Keluar
              </button>
            </div>
            
            <Button
              onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              variant="outline"
              className="gap-2 text-xs font-bold border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-xl h-9 ml-auto lg:ml-0"
              title="Urutkan Tanggal"
            >
              {sortOrder === 'desc' ? <ArrowDown className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
              {sortOrder === 'desc' ? 'Terbaru ke Lama' : 'Lama ke Terbaru'}
            </Button>

            <Button 
              onClick={handleExportPDF} 
              variant="outline" 
              className="gap-2 text-xs font-bold border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC] rounded-xl h-9 ml-auto lg:ml-0"
            >
              <Download className="w-4 h-4" /> Ekspor PDF
            </Button>
          </div>
        </div>

        {/* Search & date filter bar */}
        <div className="p-4 bg-slate-50/20 border-b border-slate-100 flex flex-wrap gap-3 items-center">
          
          {/* Account Filter Select Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">Filter Rekening:</span>
            <Select value={selectedAccountFilter} onValueChange={setSelectedAccountFilter}>
              <SelectTrigger className="h-9 text-xs w-48 bg-white border-[#E6D5B8]/60">
                <SelectValue placeholder="Semua Rekening" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Semua Rekening</SelectItem>
                {Object.keys(accounts).map(k => (
                  <SelectItem key={k} value={k}>{accounts[k].name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Cari keterangan, nominal (Rp), vendor, no invoice..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 pr-8 h-9 text-xs bg-white border-slate-200"
            />
            {search && (
              <button 
                type="button"
                onClick={() => { setSearch(''); setCurrentPage(1); }}
                className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-700"
                title="Hapus pencarian"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Scope Toggle if search has text and dates are selected */}
          {search && (dateFrom || dateTo) && (
            <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 p-1 rounded-lg shrink-0">
              <button
                type="button"
                onClick={() => { setSearchGlobalDates(false); setCurrentPage(1); }}
                className={`px-2 py-1 text-[10px] md:text-[11px] font-bold rounded-md transition-colors ${!searchGlobalDates ? 'bg-[#8C6208] text-white shadow-xs' : 'text-amber-800 hover:bg-amber-100'}`}
                title="Hanya cari di rentang tanggal yang dipilih"
              >
                📅 Periode Ini
              </button>
              <button
                type="button"
                onClick={() => { setSearchGlobalDates(true); setCurrentPage(1); }}
                className={`px-2 py-1 text-[10px] md:text-[11px] font-bold rounded-md transition-colors ${searchGlobalDates ? 'bg-[#8C6208] text-white shadow-xs' : 'text-amber-800 hover:bg-amber-100'}`}
                title="Cari transaksi ini di seluruh riwayat tanpa dibatasi tanggal"
              >
                🌐 Semua Tanggal
              </button>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">Dari:</span>
            <CustomDatePicker 
              value={dateFrom} 
              onChange={(v) => { setDateFrom(v); setSearchGlobalDates(false); }} 
              className="h-9 text-xs w-36" 
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">S/d:</span>
            <CustomDatePicker 
              value={dateTo} 
              onChange={(v) => { setDateTo(v); setSearchGlobalDates(false); }} 
              className="h-9 text-xs w-36" 
            />
          </div>
          {(dateFrom || dateTo || search || selectedAccountFilter !== 'all') && (
            <Button 
              variant="ghost" 
              onClick={() => { setDateFrom(''); setDateTo(''); setSearch(''); setSearchGlobalDates(false); setSelectedAccountFilter('all'); setCurrentPage(1); }}
              className="text-xs h-9 text-slate-500 hover:text-slate-800"
            >
              Reset Filter
            </Button>
          )}
        </div>

        {/* Search Results Summary Banner */}
        {search.trim() && (
          <div className="px-4 py-2.5 bg-amber-50/90 border-b border-amber-200 flex flex-wrap items-center justify-between gap-3 text-xs text-amber-950 animate-in fade-in">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-[#8C6208] shrink-0" />
              <span>
                Hasil pencarian untuk <strong>"{search}"</strong>: <strong>{searchSummary?.count || 0}</strong> transaksi ditemukan
                {searchGlobalDates ? ' (lintas seluruh riwayat tanggal)' : (dateFrom || dateTo ? ` (pada periode ${dateFrom || '...'} s/d ${dateTo || '...'})` : '')}
              </span>
            </div>
            {searchSummary && searchSummary.count > 0 && (
              <div className="flex items-center gap-2 font-bold text-[11px] ml-auto flex-wrap">
                {searchSummary.masuk > 0 && <span className="text-emerald-700 bg-emerald-100/70 px-2 py-0.5 rounded-full">Masuk: +Rp {fmt(searchSummary.masuk)}</span>}
                {searchSummary.keluar > 0 && <span className="text-rose-700 bg-rose-100/70 px-2 py-0.5 rounded-full">Keluar: -Rp {fmt(searchSummary.keluar)}</span>}
                <span className="text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full">Net: Rp {fmt(searchSummary.net)}</span>
              </div>
            )}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200">
                {isSelectionMode && <th className="p-2 md:p-4 w-10 md:w-12"></th>}
                <th className="p-2 md:p-4 whitespace-nowrap text-[9px] md:text-xs">Tanggal</th>
                <th className="p-2 md:p-4 whitespace-nowrap text-[9px] md:text-xs">Rekening</th>
                <th className="p-2 md:p-4 text-[9px] md:text-xs">Keterangan</th>
                <th className="p-2 md:p-4 text-right whitespace-nowrap text-[9px] md:text-xs">Jumlah</th>
                {!isSelectionMode && <th className="p-2 md:p-4 text-center whitespace-nowrap text-[9px] md:text-xs">Aksi</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-slate-400">Memuat data mutasi...</td>
                </tr>
              ) : paginatedMutations.length === 0 ? (
                <tr>
                  <td colSpan={isSelectionMode ? 5 : 4} className="text-center py-12 text-slate-400">
                    {search.trim() ? (
                      <div className="space-y-3 max-w-md mx-auto py-4">
                        <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto text-[#8C6208]">
                          <Search className="w-6 h-6" />
                        </div>
                        <p className="font-bold text-slate-800 text-sm">Tidak ditemukan transaksi untuk "{search}"</p>
                        {(dateFrom || dateTo) && !searchGlobalDates ? (
                          <div className="space-y-2">
                            <p className="text-xs text-slate-500">Mungkin transaksi yang Anda cari berada di luar rentang tanggal ({dateFrom || '...'} s/d {dateTo || '...'})?</p>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs font-bold bg-[#F9F5EC] border-[#E6D5B8] text-[#8C6208] hover:bg-[#F2E8D5] gap-1.5"
                              onClick={() => { setSearchGlobalDates(true); setCurrentPage(1); }}
                            >
                              🌐 Cari "{search}" di Seluruh Riwayat Tanggal ➔
                            </Button>
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400">Coba periksa kembali ejaan kata kunci, nama vendor, atau angka nominal yang Anda cari.</p>
                        )}
                      </div>
                    ) : (
                      'Belum ada catatan mutasi yang cocok.'
                    )}
                  </td>
                </tr>
              ) : (
                paginatedMutations.map((m, idx) => {
                  const key = getAccountKeyOfMutation(m.account);
                  const activeName = accounts[key]?.name || m.account;

                  return (
                    <tr 
                      key={m.id || idx} 
                      onClick={() => isSelectionMode && toggleSelection(m.id)}
                      className={`transition-colors ${isSelectionMode ? 'cursor-pointer' : ''} ${
                        selectedIds.has(m.id) ? 'bg-rose-50/60' : 'hover:bg-slate-50/50'
                      }`}
                    >
                      {isSelectionMode && (
                        <td className="p-2 md:p-4">
                          <Checkbox checked={selectedIds.has(m.id)} onCheckedChange={() => toggleSelection(m.id)} />
                        </td>
                      )}
                      <td className="p-2 md:p-4 font-mono text-[9px] md:text-xs text-slate-400 whitespace-nowrap">{formatDate(m.date)}</td>
                      <td className="p-2 md:p-4">
                        {(() => {
                          const idx = Object.keys(accounts).indexOf(key);
                          const colors = [
                            'bg-slate-100 text-slate-800',
                            'bg-blue-50 text-blue-700',
                            'bg-orange-50 text-orange-700',
                            'bg-purple-50 text-purple-700',
                            'bg-emerald-50 text-emerald-700'
                          ];
                          const colorClass = idx >= 0 ? colors[idx % colors.length] : 'bg-slate-100 text-slate-800';
                          return (
                            <span className={`inline-block px-2 md:px-2.5 py-0.5 rounded text-[8px] md:text-[10px] font-bold whitespace-nowrap ${colorClass}`}>
                              {activeName}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="p-2 md:p-4 text-slate-700 text-[10px] md:text-xs font-semibold">
                        {(() => {
                          const desc = m.description || '';
                          const catMatch = desc.match(/^\[(.+?)\]\s*/);
                          if (catMatch) {
                            const cat = catMatch[1];
                            const rest = desc.replace(catMatch[0], '');
                            return (
                              <>
                                <span className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded mr-1.5 ${
                                  cat === 'Bahan Baku' ? 'bg-amber-100 text-amber-800' :
                                  cat === 'Operasional' ? 'bg-blue-100 text-blue-700' :
                                  cat === 'Gaji Karyawan' ? 'bg-purple-100 text-purple-700' :
                                  cat.includes('Ongkir') ? 'bg-cyan-100 text-cyan-700' :
                                  (cat.includes('Invoice') || cat.includes('Pembayaran') || cat.includes('Verified')) ? 'bg-green-100 text-green-700' :
                                  cat.includes('Aset') ? 'bg-indigo-100 text-indigo-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>{cat}</span>
                                {rest}
                              </>
                            );
                          }
                          return desc;
                        })()}
                      </td>
                      <td className={`p-2 md:p-4 text-right font-bold text-xs md:text-sm whitespace-nowrap ${m.type === 'masuk' ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {m.type === 'masuk' ? '+' : '-'}Rp {fmt(m.amount)}
                      </td>
                      {!isSelectionMode && (
                        <td className="p-2 md:p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditMutationData({
                                  ...m,
                                  coa_allocations: (typeof m.coa_allocations === 'string' ? JSON.parse(m.coa_allocations || '[]') : (m.coa_allocations || [])).length > 0 ? (typeof m.coa_allocations === 'string' ? JSON.parse(m.coa_allocations) : m.coa_allocations) : [{ prefix: '', account_id: '', amount: '' }]
                                });
                                setShowEditMutationModal(true);
                              }}
                              className="p-1.5 text-slate-400 hover:text-[#8C6208] hover:bg-[#F9F5EC] rounded-md transition-colors"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startSelection(m.id);
                              }}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              title="Hapus"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination Controls ── */}
        {filteredMutations.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-100 bg-white flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
              <div className="w-[80px]">
                <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                  <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="w-8 h-8 rounded-xl border-slate-200 text-slate-500"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </Button>
              <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-[#8C6208] text-white font-bold text-xs shadow-sm">
                {currentPage}
              </div>
              <Button
                variant="outline"
                size="icon"
                className="w-8 h-8 rounded-xl border-slate-200 text-slate-500"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Edit Bank Account Info Modal */}
      {showEditModal && editingKey && (
        <div className="fixed inset-0 bg-black/40 -xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden border border-slate-100 text-[#302621]">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-base text-slate-800">Edit Informasi Rekening</h3>
              <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600 text-lg">×</button>
            </div>
            
            <form onSubmit={handleSaveEdit}>
              <div className="p-6 space-y-4 text-xs">
                <div className="space-y-1">
                  <Label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Nama Bank (Contoh: BCA, Mandiri)</Label>
                  <Input 
                    value={editForm.nama_bank} 
                    onChange={e => setEditForm({ ...editForm, nama_bank: e.target.value })} 
                    placeholder="Contoh: BCA"
                    className="h-9 uppercase"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Nama Rekening</Label>
                  <Input 
                    value={editForm.name} 
                    onChange={e => setEditForm({ ...editForm, name: e.target.value })} 
                    className="h-9"
                    required
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Nomor Rekening</Label>
                  <Input 
                    value={editForm.number} 
                    onChange={e => setEditForm({ ...editForm, number: e.target.value })} 
                    className="h-9"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Saldo Awal / Dana Pertama (Rp)</Label>
                  <Input 
                    type="text"
                    inputMode="numeric"
                    value={editForm.startBalance} 
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9-]/g, '');
                      setEditForm({ ...editForm, startBalance: val });
                    }} 
                    className="h-9"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Keterangan Rekening (Contoh: Saldo Utama, Saldo Operasional)</Label>
                  <Input 
                    value={editForm.label} 
                    onChange={e => setEditForm({ ...editForm, label: e.target.value })} 
                    placeholder="Contoh: Saldo Utama"
                    className="h-9"
                    required
                  />
                </div>
              </div>

              <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2 justify-end">
                {!isCreatingNewAccount && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={handleDeleteAccount}
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 mr-auto rounded-xl h-9 text-xs"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Hapus
                  </Button>
                )}
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowEditModal(false)}
                  className="rounded-xl h-9 text-xs"
                >
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#8C6208] text-white hover:bg-[#724F06] rounded-xl h-9 text-xs font-bold"
                >
                  Simpan Perubahan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Mutation Dialog Form */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 -xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-xl shadow-xl overflow-hidden border border-slate-100">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-base text-slate-800">Catat Transaksi Manual</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600 text-lg">×</button>
            </div>
            
            <form onSubmit={handleSaveManual}>
              <div className="p-6 space-y-4 text-xs">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Jenis Transaksi</Label>
                    <Select 
                      value={form.type} 
                      onValueChange={val => setForm({ ...form, type: val })}
                    >
                      <SelectTrigger className="h-9 bg-white">
                        <SelectValue placeholder="Pilih Jenis" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="masuk">Uang Masuk (Kredit)</SelectItem>
                        <SelectItem value="keluar">Uang Keluar (Debit)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="opacity-0">Hidden</Label>
                    <div className="h-9 flex items-center text-xs text-slate-500 italic">
                      *Rekening Bank diatur per COA di bawah
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Tanggal Transaksi</Label>
                    <CustomDatePicker 
                      value={form.date} 
                      onChange={val => setForm({ ...form, date: val })}
                      className="h-9"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label>Jumlah (Nominal Rp)</Label>
                    <Input 
                      type="number" 
                      placeholder="Contoh: 1500000"
                      value={form.amount} 
                      onChange={e => setForm({ ...form, amount: e.target.value })} 
                      className="h-9"
                    />
                  </div>
                </div>

                <div className="space-y-2 border border-slate-100 rounded-xl p-3 bg-slate-50/50">
                  <div className="flex justify-between items-center mb-1">
                    <Label className="font-bold">Alokasi Akun COA (Wajib)</Label>
                    <span className="text-[10px] font-medium text-slate-500">
                      Total: Rp {fmt((form.coa_allocations || []).reduce((s, c) => s + Number(c.amount || 0), 0))}
                    </span>
                  </div>
                  {(form.coa_allocations || []).map((coa, idx) => (
                    <div key={idx} className="flex gap-2 items-start relative group">
                      <div className="grid grid-cols-12 gap-2 flex-1">
                        <div className="col-span-4">
                          <Select 
                            value={coa.bank_account} 
                            onValueChange={val => {
                              const newAlloc = [...form.coa_allocations];
                              newAlloc[idx].bank_account = val;
                              setForm({ ...form, coa_allocations: newAlloc });
                            }}
                          >
                            <SelectTrigger className="h-8 text-[11px] bg-white border-slate-200">
                              <SelectValue placeholder="Pilih Bank" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              {Object.keys(accounts).map(k => (
                                <SelectItem key={k} value={accounts[k].name} className="text-[11px]">{accounts[k].name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Select 
                            value={coa.prefix} 
                            onValueChange={val => {
                              const newAlloc = [...form.coa_allocations];
                              newAlloc[idx].prefix = val;
                              newAlloc[idx].account_id = '';
                              newAlloc[idx].account_name = '';
                              setForm({ ...form, coa_allocations: newAlloc });
                            }}
                          >
                            <SelectTrigger className="h-8 text-[11px] bg-white border-slate-200 p-1 px-2">
                              <SelectValue placeholder="Ktg" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="1">1</SelectItem>
                              <SelectItem value="2">2</SelectItem>
                              <SelectItem value="3">3</SelectItem>
                              <SelectItem value="4">4</SelectItem>
                              <SelectItem value="5">5</SelectItem>
                              <SelectItem value="6">6</SelectItem>
                              <SelectItem value="7">7</SelectItem>
                              <SelectItem value="8">8</SelectItem>
                              <SelectItem value="9">9</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3">
                          <Select 
                            value={coa.account_id} 
                            onValueChange={val => {
                              const selectedCoa = coaList.find(c => c.id === val);
                              const newAlloc = [...form.coa_allocations];
                              newAlloc[idx].account_id = val;
                              newAlloc[idx].account_name = selectedCoa ? selectedCoa.name : '';
                              setForm({ ...form, coa_allocations: newAlloc });
                            }}
                          >
                            <SelectTrigger className="h-8 text-[11px] bg-white border-slate-200 text-left truncate px-2">
                              <SelectValue placeholder="Akun" />
                            </SelectTrigger>
                            <SelectContent className="bg-white max-h-48">
                              {coaList.filter(c => !coa.prefix || String(c.id).startsWith(coa.prefix)).map(c => (
                                <SelectItem key={c.id} value={c.id} className="text-[11px]">
                                  <span className="font-mono text-slate-500 mr-1">{c.id}</span>{c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3">
                          <Input 
                            type="number"
                            placeholder="Nominal"
                            value={coa.amount}
                            onChange={e => {
                              const newAlloc = [...form.coa_allocations];
                              newAlloc[idx].amount = e.target.value;
                              setForm({ ...form, coa_allocations: newAlloc });
                            }}
                            className="h-8 text-[11px] border-slate-200 px-2"
                          />
                        </div>
                      </div>
                      {form.coa_allocations.length > 1 && (
                        <button 
                          type="button"
                          onClick={() => {
                            const newAlloc = form.coa_allocations.filter((_, i) => i !== idx);
                            setForm({ ...form, coa_allocations: newAlloc });
                          }}
                          className="w-8 h-8 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-lg shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const currentTotal = Number(form.amount || 0);
                      const allocated = (form.coa_allocations || []).reduce((s, c) => s + Number(c.amount || 0), 0);
                      const remaining = Math.max(0, currentTotal - allocated).toString();
                      setForm({ ...form, coa_allocations: [...(form.coa_allocations || []), { bank_account: accounts.bca?.name || '', prefix: '', account_id: '', amount: remaining }] });
                    }}
                    className="w-full h-8 text-[11px] border-dashed text-slate-500 hover:text-[#8C6208] hover:border-[#8C6208] hover:bg-[#F9F5EC] gap-1 rounded-lg mt-2"
                  >
                    <Plus className="w-3 h-3" /> Tambah Akun COA
                  </Button>
                </div>

                <div className="space-y-1">
                  <Label>Kategori Transaksi</Label>
                  <Select 
                    value={form.kategori || ''} 
                    onValueChange={val => setForm({ ...form, kategori: val })}
                  >
                    <SelectTrigger className="h-9 bg-white">
                      <SelectValue placeholder="Pilih Kategori (Optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {KATEGORI_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label>Keterangan / Keterangan Transaksi</Label>
                  <Input 
                    placeholder="Contoh: Pembelian Bahan baku, Gaji Operator"
                    value={form.description} 
                    onChange={e => setForm({ ...form, description: e.target.value })} 
                    className="h-9"
                  />
                </div>
              </div>

              <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2 justify-end">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowAddModal(false)}
                  className="rounded-xl h-9 text-xs"
                >
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#8C6208] text-white hover:bg-[#724F06] rounded-xl h-9 text-xs"
                >
                  Simpan Catatan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Mutation Dialog Form */}
      {showEditMutationModal && editMutationData && (
        <div className="fixed inset-0 bg-black/40 -xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-xl shadow-xl overflow-hidden border border-slate-100">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-base text-slate-800">Edit Transaksi Mutasi</h3>
              <button onClick={() => setShowEditMutationModal(false)} className="text-slate-400 hover:text-slate-600 text-lg">×</button>
            </div>
            
            <form onSubmit={handleUpdateMutation}>
              <div className="p-6 space-y-4 text-xs">
                {editMutationData.reference_id && (
                  <div className="bg-amber-50 text-amber-800 p-2 text-[10px] rounded border border-amber-200">
                    Ini adalah mutasi hasil sinkronisasi otomatis (Reference ID: {editMutationData.reference_id}). Mengedit data ini tidak mengubah data asal (PO/Barang Keluar/Opex).
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Jenis Transaksi</Label>
                    <Select 
                      value={editMutationData.type} 
                      onValueChange={val => setEditMutationData({ ...editMutationData, type: val })}
                    >
                      <SelectTrigger className="h-9 bg-white">
                        <SelectValue placeholder="Pilih Jenis" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="masuk">Uang Masuk (Kredit)</SelectItem>
                        <SelectItem value="keluar">Uang Keluar (Debit)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label>Rekening Bank</Label>
                    <Select 
                      value={editMutationData.account} 
                      onValueChange={val => setEditMutationData({ ...editMutationData, account: val })}
                    >
                      <SelectTrigger className="h-9 bg-white">
                        <SelectValue placeholder="Pilih Rekening" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {Object.keys(accounts).map(k => (
                          <SelectItem key={k} value={accounts[k].name}>{accounts[k].name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Tanggal Transaksi</Label>
                    <CustomDatePicker 
                      value={normalizeDateForPicker(editMutationData.date)} 
                      onChange={val => setEditMutationData({ ...editMutationData, date: val })} 
                      className="h-9"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label>Jumlah (Nominal Rp)</Label>
                    <Input 
                      type="number" 
                      placeholder="Contoh: 1500000"
                      value={editMutationData.amount} 
                      onChange={e => setEditMutationData({ ...editMutationData, amount: e.target.value })} 
                      className="h-9"
                    />
                  </div>
                </div>

                <div className="space-y-2 border border-slate-100 rounded-xl p-3 bg-slate-50/50">
                  <div className="flex justify-between items-center mb-1">
                    <Label className="font-bold">Alokasi Akun COA (Wajib)</Label>
                    <span className="text-[10px] font-medium text-slate-500">
                      Total: Rp {fmt((editMutationData.coa_allocations || []).reduce((s, c) => s + Number(c.amount || 0), 0))}
                    </span>
                  </div>
                  {(editMutationData.coa_allocations || []).map((coa, idx) => (
                    <div key={idx} className="flex gap-2 items-start relative group">
                      <div className="flex-1 grid grid-cols-12 gap-2">
                        <div className="col-span-3">
                          <Select 
                            value={coa.prefix} 
                            onValueChange={val => {
                              const newAlloc = [...editMutationData.coa_allocations];
                              newAlloc[idx].prefix = val;
                              newAlloc[idx].account_id = '';
                              newAlloc[idx].account_name = '';
                              setEditMutationData({ ...editMutationData, coa_allocations: newAlloc });
                            }}
                          >
                            <SelectTrigger className="h-8 text-[11px] bg-white border-slate-200">
                              <SelectValue placeholder="Kategori" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="1">1 - Aset</SelectItem>
                              <SelectItem value="2">2 - Kewajiban</SelectItem>
                              <SelectItem value="3">3 - Ekuitas</SelectItem>
                              <SelectItem value="4">4 - Pendapatan</SelectItem>
                              <SelectItem value="5">5 - HPP</SelectItem>
                              <SelectItem value="6">6 - Beban</SelectItem>
                              <SelectItem value="7">7 - Pdt Lain</SelectItem>
                              <SelectItem value="8">8 - Bbn Lain</SelectItem>
                              <SelectItem value="9">9 - Pajak</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-5">
                          <Select 
                            value={coa.account_id} 
                            onValueChange={val => {
                              const selectedCoa = coaList.find(c => c.id === val);
                              const newAlloc = [...editMutationData.coa_allocations];
                              newAlloc[idx].account_id = val;
                              newAlloc[idx].account_name = selectedCoa ? selectedCoa.name : '';
                              setEditMutationData({ ...editMutationData, coa_allocations: newAlloc });
                            }}
                          >
                            <SelectTrigger className="h-8 text-[11px] bg-white border-slate-200 text-left truncate">
                              <SelectValue placeholder="Pilih Akun" />
                            </SelectTrigger>
                            <SelectContent className="bg-white max-h-48">
                              {coaList.filter(c => !coa.prefix || String(c.id).startsWith(coa.prefix)).map(c => (
                                <SelectItem key={c.id} value={c.id} className="text-[11px]">
                                  <span className="font-mono text-slate-500 mr-1">{c.id}</span>{c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-4">
                          <Input 
                            type="number"
                            placeholder="Nominal"
                            value={coa.amount}
                            onChange={e => {
                              const newAlloc = [...editMutationData.coa_allocations];
                              newAlloc[idx].amount = e.target.value;
                              setEditMutationData({ ...editMutationData, coa_allocations: newAlloc });
                            }}
                            className="h-8 text-[11px] border-slate-200"
                          />
                        </div>
                      </div>
                      {editMutationData.coa_allocations.length > 1 && (
                        <button 
                          type="button"
                          onClick={() => {
                            const newAlloc = editMutationData.coa_allocations.filter((_, i) => i !== idx);
                            setEditMutationData({ ...editMutationData, coa_allocations: newAlloc });
                          }}
                          className="w-8 h-8 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-lg shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const currentTotal = Number(editMutationData.amount || 0);
                      const allocated = (editMutationData.coa_allocations || []).reduce((s, c) => s + Number(c.amount || 0), 0);
                      const remaining = Math.max(0, currentTotal - allocated).toString();
                      setEditMutationData({ ...editMutationData, coa_allocations: [...(editMutationData.coa_allocations || []), { prefix: '', account_id: '', amount: remaining }] });
                    }}
                    className="w-full h-8 text-[11px] border-dashed text-slate-500 hover:text-[#8C6208] hover:border-[#8C6208] hover:bg-[#F9F5EC] gap-1 rounded-lg mt-2"
                  >
                    <Plus className="w-3 h-3" /> Tambah Akun COA
                  </Button>
                </div>

                <div className="space-y-1">
                  <Label>Kategori Transaksi (Lama)</Label>
                  <Select 
                    value={editMutationData.kategori || ''} 
                    onValueChange={val => setEditMutationData({ ...editMutationData, kategori: val })}
                  >
                    <SelectTrigger className="h-9 bg-white">
                      <SelectValue placeholder="Pilih Kategori (Optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {KATEGORI_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label>Keterangan / Keterangan Transaksi</Label>
                  <Input 
                    placeholder="Contoh: Pembelian Bahan baku, Gaji Operator"
                    value={editMutationData.description} 
                    onChange={e => setEditMutationData({ ...editMutationData, description: e.target.value })} 
                    className="h-9"
                  />
                </div>
              </div>

              <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2 justify-end">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowEditMutationModal(false)}
                  className="rounded-xl h-9 text-xs"
                >
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#8C6208] text-white hover:bg-[#724F06] rounded-xl h-9 text-xs"
                >
                  Simpan Perubahan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
